import React, { useMemo } from 'react';
import { Tag, Timeline, Typography } from 'antd';
import { ArrowSquareOut } from '@phosphor-icons/react';
import './oneButton-css/GoodLeapTimelineModalContent.css';

export type GoodLeapTimelineStep = {
    title: string;
    dateComplete?: string;
    status: string;
    items: string[];
};

function readObj(v: unknown): Record<string, unknown> {
    return v != null && typeof v === 'object' ? (v as Record<string, unknown>) : {};
}

function goodLeapTimelineItemToString(x: unknown): string {
    if (x == null) return '';
    if (typeof x === 'string' || typeof x === 'number' || typeof x === 'boolean') return String(x);
    if (typeof x === 'object') {
        const o = x as Record<string, unknown>;
        const m = o.message ?? o.Message ?? o.text ?? o.Text ?? o.description ?? o.Description;
        if (m != null && String(m).trim()) return String(m).trim();
    }
    return typeof x === 'object' ? JSON.stringify(x) : String(x);
}

/** Walk common API envelopes until `timeline.sections` is found (Trinity + PascalCase + nested `data`). */
function coalesceTimelineSectionArray(res: unknown): unknown[] | null {
    const root = readObj(res);
    const layers: Record<string, unknown>[] = [
        root,
        readObj(root.data),
        readObj(root.Data),
        readObj(readObj(root.data).data),
        readObj(readObj(root.Data).data),
        readObj(readObj(root.Data).Data),
    ];
    for (const layer of layers) {
        const timeline = readObj(layer.timeline ?? layer.Timeline);
        const sections = timeline.sections ?? timeline.Sections;
        if (Array.isArray(sections)) return sections;
    }
    return null;
}

/** Trinity `TimelineComponent` input: `timelineData.data.timeline.sections` (see `detail-cell-renderer.openTimeline`). */
export function extractGoodLeapTimelineSections(res: unknown): GoodLeapTimelineStep[] {
    const sections = coalesceTimelineSectionArray(res);
    if (!Array.isArray(sections)) return [];

    return sections.map((raw) => {
        const o = readObj(raw);
        let rawItems: unknown = o.items ?? o.Items ?? o.subMessages ?? o.SubMessages;
        if (!Array.isArray(rawItems) || rawItems.length === 0) {
            const ch = o.children ?? o.Children;
            if (Array.isArray(ch) && ch.length) rawItems = ch;
        }
        const items: string[] = Array.isArray(rawItems)
            ? rawItems.map((x) => goodLeapTimelineItemToString(x)).filter(Boolean)
            : [];
        return {
            title: String(o.title ?? o.Title ?? '').trim() || '—',
            dateComplete: (() => {
                const d = o.dateComplete ?? o.DateComplete;
                return d != null && String(d).trim() ? String(d).trim() : undefined;
            })(),
            status: String(o.status ?? o.Status ?? '').trim() || '—',
            items,
        };
    });
}

/** Parity with Angular `timeline.component#determineColor`. */
export function goodLeapTimelineDotColor(status: string): 'green' | 'blue' | 'red' | 'gray' {
    const s = status.trim().toUpperCase().replace(/\s+/g, '_');
    if (s === 'COMPLETE') return 'green';
    if (s === 'IN_PROGRESS') return 'blue';
    if (s === 'FAILED') return 'red';
    return 'gray';
}

function goodLeapStatusTag(status: string) {
    const s = status.trim().toUpperCase().replace(/\s+/g, '_');
    if (s === 'COMPLETE') {
        return (
            <Tag className="obd-gl-timeline__status-tag" color="success">
                {status}
            </Tag>
        );
    }
    if (s === 'IN_PROGRESS') {
        return (
            <Tag className="obd-gl-timeline__status-tag" color="processing">
                {status}
            </Tag>
        );
    }
    if (s === 'FAILED') {
        return (
            <Tag className="obd-gl-timeline__status-tag" color="error">
                {status}
            </Tag>
        );
    }
    return (
        <Tag className="obd-gl-timeline__status-tag" color="default">
            {status}
        </Tag>
    );
}

/** Parity with Angular `timeline.component#formatToEST` (Luxon UTC → America/New_York + literal ` EST`). */
export function formatGoodLeapTimelineDateEst(iso?: string): string {
    if (!iso?.trim()) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const formatted = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    })
        .format(d)
        .replace(',', '');
    return `${formatted} EST`;
}

export type GoodLeapTimelineModalContentProps = {
    response: unknown;
    applicationId: string;
    applicationLink: string | null | undefined;
};

const GoodLeapTimelineModalContent: React.FC<GoodLeapTimelineModalContentProps> = ({
    response,
    applicationId,
    applicationLink,
}) => {
    const steps = useMemo(() => extractGoodLeapTimelineSections(response), [response]);

    const openLink = () => {
        const url = applicationLink?.trim();
        if (url && /^https?:\/\//i.test(url)) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div className="obd-gl-timeline">
            {applicationId ? (
                <div className="obd-gl-timeline__app-banner">
                    <span className="obd-gl-timeline__app-label">
                        Application ID: {applicationId}
                        {applicationLink && /^https?:\/\//i.test(applicationLink.trim()) ? (
                            <button
                                type="button"
                                className="obd-gl-timeline__app-link"
                                onClick={openLink}
                                aria-label="Open application in new tab"
                            >
                                <ArrowSquareOut size={15} weight="bold" aria-hidden />
                            </button>
                        ) : null}
                    </span>
                </div>
            ) : null}

            {steps.length === 0 ? (
                <Typography.Text type="secondary" className="obd-gl-timeline__empty">
                    No timeline data available for this application.
                </Typography.Text>
            ) : (
                <Timeline
                    className="obd-gl-timeline__ant"
                    items={steps.map((step, idx) => ({
                        key: `${idx}-${step.title}`,
                        color: goodLeapTimelineDotColor(step.status),
                        children: (
                            <div className="obd-gl-timeline__step">
                                <strong className="obd-gl-timeline__step-title">{step.title}</strong>
                                {step.dateComplete ? (
                                    <div className="obd-gl-timeline__step-date">{formatGoodLeapTimelineDateEst(step.dateComplete)}</div>
                                ) : null}
                                <div className="obd-gl-timeline__step-status-row">
                                    <span className="obd-gl-timeline__step-status-label">Status</span>
                                    {goodLeapStatusTag(step.status)}
                                </div>
                                {step.items.length ? (
                                    <ul className="obd-gl-timeline__step-items">
                                        {step.items.map((item, j) => (
                                            <li key={`${j}-${item.slice(0, 40)}`}>{item}</li>
                                        ))}
                                    </ul>
                                ) : null}
                            </div>
                        ),
                    }))}
                />
            )}
        </div>
    );
};

export default GoodLeapTimelineModalContent;
